import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import * as stylesheet from 'stylesheet-helpers';
import { useState, useEffect } from 'react';
import { set } from '@elliemae/ds-utilities/utils';
import getColumnFromProperty from './getColumnNameFromProperty.js';
import { getScrollbarWidth } from '../../utilities/getScrollbarSize.js';

var SCROLLBAR_WIDTH = getScrollbarWidth();
var RowStylesheetHelper = function RowStylesheetHelper(_ref) {
  var grid = _ref.grid,
      className = _ref.className;

  var _stylesheet$create = stylesheet.create(),
      styleSheet = _stylesheet$create.styleSheet,
      styleSheetElement = _stylesheet$create.styleSheetElement;

  var update = function update(width) {
    var widths = typeof width === 'string' ? width : "".concat(width, "px");
    stylesheet.updateProperties(window, styleSheet, className, width ? {
      width: widths,
      minWidth: widths,
      maxWidth: widths
    } : {
      width: null,
      minWidth: null,
      maxWidth: null
    });

    var _grid$getState = grid.getState(),
        hasScroll = _grid$getState.hasScroll;

    var headerWidth = "".concat(width + (hasScroll ? SCROLLBAR_WIDTH : 0), "px");
    stylesheet.updateProperties(window, styleSheet, "".concat(className, "-header"), width ? {
      width: headerWidth,
      minWidth: headerWidth,
      maxWidth: headerWidth
    } : {
      width: null,
      minWidth: null,
      maxWidth: null
    });
  };

  var remove = function remove() {
    return styleSheetElement.remove();
  };

  var getRowClassName = function getRowClassName() {
    return className;
  };

  return {
    update: update,
    remove: remove,
    getRowClassName: getRowClassName
  };
};

var getColumnClassName = function getColumnClassName(_ref2) {
  var globalId = _ref2.globalId,
      column = _ref2.column,
      _ref2$isHeader = _ref2.isHeader,
      isHeader = _ref2$isHeader === void 0 ? false : _ref2$isHeader,
      getId = _ref2.getId;
  return "column-".concat(globalId, "-").concat(getId(column)).concat(isHeader ? '-header' : '');
};

function setWidth(_ref3) {
  var styleSheet = _ref3.styleSheet,
      className = _ref3.className,
      width = _ref3.width,
      minWidth = _ref3.minWidth;
  var widths;
  if (typeof width === 'string') widths = width;
  if (typeof width === 'number') widths = "".concat(width, "px");
  var minWidths;
  if (typeof minWidth === 'string') minWidths = minWidth;
  if (typeof minWidth === 'number') minWidths = "".concat(minWidth, "px");
  stylesheet.updateProperties(window, styleSheet, className, {
    width: "".concat(widths || minWidths),
    minWidth: "".concat(widths || minWidths),
    // minWidth: `${minWidths || widths}`,
    maxWidth: "".concat(widths || minWidths)
  });
}

function updateStyle(_ref4) {
  var styleSheet = _ref4.styleSheet,
      className = _ref4.className,
      style = _ref4.style;
  stylesheet.updateProperties(window, styleSheet, className, style);
}

function columnSizesHelperImpl(_ref5) {
  var globalId = _ref5.globalId,
      _ref5$getId = _ref5.getId,
      getId = _ref5$getId === void 0 ? function (_ref6) {
    var property = _ref6.property;
    return getColumnFromProperty(property);
  } : _ref5$getId;

  var _stylesheet$create2 = stylesheet.create(),
      styleSheetElement = _stylesheet$create2.styleSheetElement,
      styleSheet = _stylesheet$create2.styleSheet;

  var updateBodyHeaderWidth = function updateBodyHeaderWidth(_ref7) {
    var className = _ref7.className,
        headerClassName = _ref7.headerClassName,
        width = _ref7.width,
        minWidth = _ref7.minWidth;
    setWidth({
      styleSheet: styleSheet,
      className: className,
      width: width,
      minWidth: minWidth
    });
    setWidth({
      styleSheet: styleSheet,
      className: headerClassName,
      width: width,
      minWidth: minWidth,
      isHeader: true
    });
  };

  return {
    decorateColumns: function decorateColumns(columns, grid) {
      var _grid$getInstance = grid.getInstance(),
          minColumnWidth = _grid$getInstance.props.minColumnWidth;

      return columns.map(function (column) {
        var cellClassName = getColumnClassName({
          globalId: globalId,
          getId: getId,
          column: column
        });
        var headerClassName = "".concat(cellClassName, "-header");
        set(column, 'header.props.className', headerClassName);
        set(column, 'cell.props.className', cellClassName);

        column.updateBodyHeaderWidth = function (width, minWidth) {
          updateBodyHeaderWidth({
            className: cellClassName,
            headerClassName: headerClassName,
            width: width,
            minWidth: minWidth
          });
        };

        column.updateWidth = function (width, minWidth) {
          setWidth({
            styleSheet: styleSheet,
            className: cellClassName,
            width: width,
            minWidth: minWidth
          });
        };

        column.updateStyle = function (style) {
          return updateStyle({
            styleSheet: styleSheet,
            className: cellClassName,
            style: style
          });
        };

        column.headerHookEffects = [function () {
          useEffect(function () {
            var _grid$getInstance2 = grid.getInstance(),
                resizedColumns = _grid$getInstance2.resizedColumns;

            var resizedWidth = resizedColumns.current[column.uuid];
            updateBodyHeaderWidth({
              className: cellClassName,
              headerClassName: headerClassName,
              width: resizedWidth || column.width || minColumnWidth,
              minWidth: resizedWidth || column.minWidth || minColumnWidth || column.width
            });
          }, [column.uuid]);
        }];
        return column;
      });
    },
    getColumnCellClassName: function getColumnCellClassName(column) {
      return getColumnClassName({
        globalId: globalId,
        getId: getId,
        column: column
      });
    },
    getColumnHeaderClassName: function getColumnHeaderClassName(column) {
      return "".concat(getColumnClassName({
        globalId: globalId,
        getId: getId,
        column: column
      }), "-header");
    },
    cleanup: function cleanup() {
      styleSheetElement.remove();
    }
  };
}

function useStylesheetHelpers(grid) {
  var _grid$props = grid.props,
      bindColumnsSizeTo = _grid$props.bindColumnsSizeTo,
      bindRowSizeTo = _grid$props.bindRowSizeTo;

  var _useState = useState(function () {
    return columnSizesHelperImpl({
      globalId: bindColumnsSizeTo || grid.uuid,
      getId: function getId(_ref8) {
        var uuid = _ref8.uuid;
        return getColumnFromProperty(uuid);
      }
    });
  }),
      _useState2 = _slicedToArray(_useState, 1),
      columnsStylesheet = _useState2[0];

  var rowClassName = "row-".concat(bindRowSizeTo || grid.uuid);

  var _useState3 = useState(function () {
    return RowStylesheetHelper({
      grid: grid,
      className: rowClassName
    });
  }),
      _useState4 = _slicedToArray(_useState3, 1),
      rowsStylesheet = _useState4[0];

  return {
    columnsStylesheet: columnsStylesheet,
    rowsStylesheet: rowsStylesheet,
    rowClassName: rowClassName
  };
}

export { RowStylesheetHelper, useStylesheetHelpers };
//# sourceMappingURL=useStylesheetHelpers.js.map
